'use client';

import { useEffect, useRef, useState } from 'react';
import type { KlaviyoReviewsResponse } from '@/lib/reviews';
import { ProductReviews, ProductReviewsSkeleton } from './product-reviews';

const REVIEWS_ROOT_MARGIN = '800px 0px';

async function fetchInitialReviews(productId: string, signal: AbortSignal) {
	const url = new URL(`/api/reviews/${productId}`, window.location.origin);
	url.searchParams.set('limit', '5');
	url.searchParams.set('offset', '0');

	const response = await fetch(url.toString(), { signal });
	if (!response.ok) {
		throw new Error(`Failed to fetch product reviews (${response.status})`);
	}

	return (await response.json()) as KlaviyoReviewsResponse;
}

export function ProductReviewsContainer({
	productId,
	initialData,
	moneyLocale,
}: {
	productId: string;
	initialData?: KlaviyoReviewsResponse | null;
	moneyLocale: string;
}) {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [data, setData] = useState<KlaviyoReviewsResponse | null>(initialData ?? null);
	const [loaded, setLoaded] = useState(Boolean(initialData));
	const [shouldLoad, setShouldLoad] = useState(Boolean(initialData));

	useEffect(() => {
		if (initialData) return;
		if (shouldLoad) return;

		const node = containerRef.current;
		if (!node) return;

		if (!('IntersectionObserver' in window)) {
			setShouldLoad(true);
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (!entries.some((entry) => entry.isIntersecting)) return;
				setShouldLoad(true);
				observer.disconnect();
			},
			{ rootMargin: REVIEWS_ROOT_MARGIN },
		);

		observer.observe(node);

		return () => {
			observer.disconnect();
		};
	}, [initialData, shouldLoad]);

	useEffect(() => {
		if (initialData) return;
		if (!shouldLoad) return;

		const controller = new AbortController();

		void fetchInitialReviews(productId, controller.signal)
			.then((nextData) => {
				if (controller.signal.aborted) return;
				setData(nextData);
				setLoaded(true);
			})
			.catch(() => {
				if (controller.signal.aborted) return;
				setLoaded(true);
			});

		return () => {
			controller.abort();
		};
	}, [initialData, productId, shouldLoad]);

	if (!loaded) {
		return (
			<div ref={containerRef} className="container-page mt-10">
				<ProductReviewsSkeleton />
			</div>
		);
	}

	if (!data?.reviews || data.reviews.length === 0) return null;

	return (
		<div className="container-page mt-10">
			<ProductReviews
				reviews={data.reviews}
				product={data.product}
				productId={productId}
				moneyLocale={moneyLocale}
			/>
		</div>
	);
}
